import { buildSympyCode, parseNodeToSympy, parseSympyResult } from "@corca/sympy";

import { pyodideManager } from "@/components/pyodide/PyodideManager";
import { nodeFromJson } from "@/models/nodes/nodeFromJson";
import { createLogger } from "@/utils/logger";
import { trySympy } from "@/utils/sympyErrors";

import type { SympyActionType } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeJson } from "@corca/data";

const logger = createLogger("sympyAction");

export async function runSympyAction(doc: DocumentModel, action: SympyActionType) {
  const {
    selection: { isContinuousSelection, selectedNodes },
  } = doc;

  if (
    !isContinuousSelection ||
    selectedNodes.length === 0 ||
    !pyodideManager.isInitialized ||
    pyodideManager.currentStatus === "starting"
  ) {
    return;
  }

  const nodes = [...selectedNodes];
  const firstNode = nodes[0];
  const { parent } = firstNode;

  if (!parent) return;

  const inlineNodeJson: NodeJson = {
    type: "inline",
    props: {},
    children: nodes.map((n) => n.toJSON()),
  };

  const parsed = trySympy(() => parseNodeToSympy(inlineNodeJson));
  if ("error" in parsed) {
    logger.warn(`${action}: Parse error`, parsed.error);
    return;
  }

  parsed.warnings.forEach((warning) => logger.warn(`${action}: ${warning}`));

  const { expr, variables, symbolNodes, hasDecimal } = parsed;

  const buildResult = trySympy(() => buildSympyCode(action, expr, variables));
  if ("error" in buildResult) {
    logger.warn(`${action}: Build code error`, buildResult.error);
    return;
  }

  try {
    const res = await pyodideManager.executeSympyCode(buildResult.expr);

    const { result } = res;
    if (result) {
      const parsedResult = trySympy(() =>
        parseSympyResult(result, undefined, symbolNodes, hasDecimal),
      );
      if ("error" in parsedResult) {
        logger.warn(`${action}: Parse result error`, parsedResult.error);
        return;
      }
      parsedResult.warnings.forEach((warning) => logger.warn(warning));
      const resultJson = parsedResult.nodes;

      doc.transact(() => {
        // Verify nodes are still valid/attached
        if (!firstNode.parent || firstNode.parent !== parent) return;

        // Create new nodes from JSON
        const newNodes = resultJson
          .map((json) => nodeFromJson(doc, json, { resetIds: true }))
          .filter((n) => n !== undefined);

        if (newNodes.length > 0) {
          // Insert new nodes before the first selected node
          newNodes.forEach((node) => {
            if (node) firstNode.insertSiblingBefore(node);
          });
        }

        // Remove old nodes
        nodes.forEach((node) => {
          doc.removeNode(node); // Checks if node is still in doc
        });

        doc.selection.resetSelection();
      }, `${action}Selection`);
    }
  } catch (e) {
    console.error(`Sympy action (${action}): Error`, e);
  }
}
